// node:path のブラウザ代替（posix のみ）。esbuild の alias で node:path をこれへ差し替える。
// 既存 lib/*.mjs が使うのは join/resolve/dirname/basename/extname/relative/isAbsolute/sep のみ。
// VFS のパスは常に絶対（/vfs/...）。cwd は '/vfs' を既定とする（相対入力は実運用では発生しない）。

const CWD = '/vfs';

function normalizeStringPosix(pathStr, allowAboveRoot) {
    let res = '';
    let lastSegmentLength = 0;
    let lastSlash = -1;
    let dots = 0;
    let code = 0;
    for (let i = 0; i <= pathStr.length; ++i) {
        if (i < pathStr.length) code = pathStr.charCodeAt(i);
        else if (code === 47 /* / */) break;
        else code = 47;
        if (code === 47) {
            if (lastSlash === i - 1 || dots === 1) {
                // noop
            } else if (lastSlash !== i - 1 && dots === 2) {
                if (res.length < 2 || lastSegmentLength !== 2 || res.charCodeAt(res.length - 1) !== 46 || res.charCodeAt(res.length - 2) !== 46) {
                    if (res.length > 2) {
                        const lastSlashIndex = res.lastIndexOf('/');
                        if (lastSlashIndex !== res.length - 1) {
                            res = lastSlashIndex === -1 ? '' : res.slice(0, lastSlashIndex);
                            lastSegmentLength = res.length - 1 - res.lastIndexOf('/');
                            lastSlash = i;
                            dots = 0;
                            continue;
                        }
                    } else if (res.length === 2 || res.length === 1) {
                        res = '';
                        lastSegmentLength = 0;
                        lastSlash = i;
                        dots = 0;
                        continue;
                    }
                }
                if (allowAboveRoot) {
                    res += res.length > 0 ? '/..' : '..';
                    lastSegmentLength = 2;
                }
            } else {
                if (res.length > 0) res += '/' + pathStr.slice(lastSlash + 1, i);
                else res = pathStr.slice(lastSlash + 1, i);
                lastSegmentLength = i - lastSlash - 1;
            }
            lastSlash = i;
            dots = 0;
        } else if (code === 46 /* . */ && dots !== -1) {
            ++dots;
        } else {
            dots = -1;
        }
    }
    return res;
}

export function resolve(...args) {
    let resolvedPath = '';
    let resolvedAbsolute = false;
    for (let i = args.length - 1; i >= -1 && !resolvedAbsolute; i--) {
        const p = i >= 0 ? args[i] : CWD;
        if (typeof p !== 'string') throw new TypeError('Path must be a string');
        if (p.length === 0) continue;
        resolvedPath = p + '/' + resolvedPath;
        resolvedAbsolute = p.charCodeAt(0) === 47;
    }
    resolvedPath = normalizeStringPosix(resolvedPath, !resolvedAbsolute);
    if (resolvedAbsolute) return '/' + resolvedPath;
    return resolvedPath.length > 0 ? resolvedPath : '.';
}

export function normalize(p) {
    if (p.length === 0) return '.';
    const isAbs = p.charCodeAt(0) === 47;
    const trailingSep = p.charCodeAt(p.length - 1) === 47;
    let out = normalizeStringPosix(p, !isAbs);
    if (out.length === 0 && !isAbs) out = '.';
    if (out.length > 0 && trailingSep) out += '/';
    return (isAbs ? '/' : '') + out;
}

export function join(...args) {
    if (args.length === 0) return '.';
    let joined;
    for (const arg of args) {
        if (typeof arg !== 'string') throw new TypeError('Path must be a string');
        if (arg.length > 0) joined = joined === undefined ? arg : joined + '/' + arg;
    }
    if (joined === undefined) return '.';
    return normalize(joined);
}

export function isAbsolute(p) {
    return p.length > 0 && p.charCodeAt(0) === 47;
}

export function dirname(p) {
    if (p.length === 0) return '.';
    const hasRoot = p.charCodeAt(0) === 47;
    let end = -1;
    let matchedSlash = true;
    for (let i = p.length - 1; i >= 1; --i) {
        if (p.charCodeAt(i) === 47) {
            if (!matchedSlash) { end = i; break; }
        } else {
            matchedSlash = false;
        }
    }
    if (end === -1) return hasRoot ? '/' : '.';
    if (hasRoot && end === 1) return '//';
    return p.slice(0, end);
}

export function basename(p, ext) {
    let start = 0;
    let end = -1;
    let matchedSlash = true;
    for (let i = p.length - 1; i >= 0; --i) {
        if (p.charCodeAt(i) === 47) {
            if (!matchedSlash) { start = i + 1; break; }
        } else if (end === -1) {
            matchedSlash = false;
            end = i + 1;
        }
    }
    if (end === -1) return '';
    let base = p.slice(start, end);
    if (ext && base.endsWith(ext) && base !== ext) base = base.slice(0, base.length - ext.length);
    return base;
}

export function extname(p) {
    let startDot = -1;
    let startPart = 0;
    let end = -1;
    let matchedSlash = true;
    let preDotState = 0;
    for (let i = p.length - 1; i >= 0; --i) {
        const code = p.charCodeAt(i);
        if (code === 47) {
            if (!matchedSlash) { startPart = i + 1; break; }
            continue;
        }
        if (end === -1) { matchedSlash = false; end = i + 1; }
        if (code === 46) {
            if (startDot === -1) startDot = i;
            else if (preDotState !== 1) preDotState = 1;
        } else if (startDot !== -1) {
            preDotState = -1;
        }
    }
    if (startDot === -1 || end === -1 || preDotState === 0 || (preDotState === 1 && startDot === end - 1 && startDot === startPart + 1)) {
        return '';
    }
    return p.slice(startDot, end);
}

export function relative(from, to) {
    if (from === to) return '';
    from = resolve(from);
    to = resolve(to);
    if (from === to) return '';
    const fromParts = from.slice(1).split('/').filter(Boolean);
    const toParts = to.slice(1).split('/').filter(Boolean);
    let i = 0;
    while (i < fromParts.length && i < toParts.length && fromParts[i] === toParts[i]) i++;
    const up = fromParts.slice(i).map(() => '..');
    return [...up, ...toParts.slice(i)].join('/');
}

export const sep = '/';
export const delimiter = ':';

const posix = { resolve, normalize, join, isAbsolute, dirname, basename, extname, relative, sep, delimiter };
posix.posix = posix;
export default posix;
